import { css } from '@emotion/react';
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { formatDateTime } from '@kaiizen/kai-presenters';
import {
  ApiScan,
  ApiScanNote,
  canUserDeleteScanNote,
  getDeletedParentScanNoteIds,
  isAuthoredRemarkScanNote,
  ScanNoteType,
} from '@kaiizen/kai-pro-api';
import {
  ScanMutators,
  useAuth,
  useScanEditingContextSlice,
  useScanPartNotes,
  useUser,
} from '@kaiizen/kai-pro-api-react-state';
import {
  ScanPartOptimized,
  ScanRootOptimized,
} from '@kaiizen/kai-scan-template-system';

import { TrashIcon } from '@/Client/Icons';
import { logError } from '@/Client/Lib/logger';
import { NOTES_CHARACTER_LIMIT } from '@/Client/noteConstants';
import { useConfirmationModal } from '@/Client/State/ConfirmationModalContext';
import { useToast } from '@/Client/State/ToastContext';
import { isScrolledToBottom, scrollToBottom } from '@/lib/scrollLib';
import { COLORS2, LAYOUT2, TYPOGRAPHY2 } from '../../Styles';
import { Button } from '../../Widgets/Button';
import { FlyOut } from '../../Widgets/Flyout';

/** Run callback after FlyOut drawer has mounted and laid out (double rAF). */
const runAfterDrawerReady = (callback: () => void) => {
  requestAnimationFrame(() => requestAnimationFrame(callback));
};

export type ShowNotesOptions = {
  scanPart?: ScanPartOptimized;
  allowedNoteTypes?: ScanNoteType[];
};

export type NotesPanelProps = ShowNotesOptions & {
  scanMutators: ScanMutators;
  open: boolean;
  flyOutClosed: () => void;
  scanRoot: ScanRootOptimized;
  scan: ApiScan;
};

const NOTES_CONTAINER_STYLE = css`
  label: NOTES_CONTAINER_STYLE;
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const NOTES_HISTORY_STYLE = css`
  label: NOTES_HISTORY_STYLE;
  flex: 1;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
`;

const NOTE_ITEM_STYLE = css`
  label: NOTE_ITEM_STYLE;
  padding: ${LAYOUT2.spacing1p5}px;
  margin-bottom: ${LAYOUT2.spacing1p5}px;
  background: var(--background);
  border: 1px solid var(--border2);
  color: var(--text);
  ${TYPOGRAPHY2.small}
`;

const NOTE_ITEM_BASE_STYLE = css`
  ${NOTE_ITEM_STYLE}
  display: flex;
  flex-direction: column;
  gap: ${LAYOUT2.spacing1}px;
`;

const NOTE_ITEM_APPROVAL_STYLE = css`
  ${NOTE_ITEM_STYLE}
  background: var(--backgroundGreen);
  border-color: var(--backgroundGreen2);
`;

const NOTE_ITEM_EDIT_REQUEST_STYLE = css`
  ${NOTE_ITEM_STYLE}
  background: var(--backgroundYellow);
  border-color: var(--backgroundYellow2);
`;

const NOTE_ITEM_EDIT_RESOLUTION_STYLE = css`
  ${NOTE_ITEM_STYLE}
  background: var(--backgroundBlue);
  border-color: var(--backgroundBlue3);
`;

const NOTE_HEADER_STYLE = css`
  label: NOTE_HEADER_STYLE;
  ${TYPOGRAPHY2.xSmall}
  color: var(--text2);
`;

const NOTE_TYPE_BADGE_STYLE = css`
  label: NOTE_TYPE_BADGE_STYLE;
  ${TYPOGRAPHY2.xSmallBold}
  padding: ${LAYOUT2.spacingP5}px ${LAYOUT2.spacing1}px;
  margin-bottom: ${LAYOUT2.spacing1}px;
  display: inline-block;
`;

const APPROVAL_BADGE_STYLE = css`
  ${NOTE_TYPE_BADGE_STYLE}
  background: ${COLORS2.alwaysDark.text.green};
  color: ${COLORS2.alwaysDark.text.white};
`;

const EDIT_REQUEST_BADGE_STYLE = css`
  ${NOTE_TYPE_BADGE_STYLE}
  background: ${COLORS2.alwaysDark.primary};
  color: ${COLORS2.alwaysDark.text.black};
`;

const EDIT_RESOLUTION_BADGE_STYLE = css`
  ${NOTE_TYPE_BADGE_STYLE}
  background: ${COLORS2.alwaysDark.blue};
  color: ${COLORS2.alwaysDark.text.black};
`;

const SCOPE_NOTE_BADGE_STYLE = css`
  ${NOTE_TYPE_BADGE_STYLE}
  background: ${COLORS2.alwaysDark.text.black};
  color: ${COLORS2.alwaysDark.text.white};
`;

const DELETED_NOTE_BADGE_STYLE = css`
  ${NOTE_TYPE_BADGE_STYLE}
  background: var(--border2);
  color: var(--textOnBackground);
`;

const DELETED_NOTE_STYLE = css`
  ${NOTE_ITEM_STYLE}
  background: var(--background2);
  border-color: var(--border2);
  color: var(--text2);
`;

const NOTE_TITLE_ROW_STYLE = css`
  label: NOTE_TITLE_ROW_STYLE;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: ${LAYOUT2.spacing1}px;
`;

const NOTE_META_ROW_STYLE = css`
  label: NOTE_META_ROW_STYLE;
  display: flex;
  align-items: baseline;
  gap: ${LAYOUT2.spacing1}px;
`;

const DELETED_EXPLAINER_STYLE = css`
  label: DELETED_EXPLAINER_STYLE;
  ${TYPOGRAPHY2.xSmall}
  color: var(--text2);
`;

const DELETE_NOTE_BUTTON_STYLE = css`
  label: DELETE_NOTE_BUTTON_STYLE;
  border: none;
  background: transparent;
  padding: 0;
  cursor: pointer;
  line-height: 0;
  color: var(--text);
  opacity: 0.75;

  &:hover {
    opacity: 1;
  }

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }
`;

const NOTE_BODY_STYLE = css`
  label: NOTE_BODY_STYLE;
  color: var(--text);
  white-space: pre-wrap;
`;

const FIXED_INPUT_AREA_STYLE = css`
  label: FIXED_INPUT_AREA_STYLE;
  border-top: 1px solid var(--border2);
  padding-top: ${LAYOUT2.spacing2}px;
  background: var(--background);
`;

const TEXTAREA_STYLE = css`
  label: TEXTAREA_STYLE;
  width: 100%;
  min-height: 80px;
  padding: ${LAYOUT2.spacing1}px;
  border: 1px solid var(--border2);
  background: var(--background);
  color: var(--text);
  font-family: inherit;
  ${TYPOGRAPHY2.small}
  resize: vertical;

  &:focus {
    outline: none;
    border-color: var(--textGreen);
  }
`;

const CHARACTER_COUNT_STYLE = css`
  label: CHARACTER_COUNT_STYLE;
  text-align: right;
  padding-top: 2px;
  margin-bottom: ${LAYOUT2.spacing1}px;
  ${TYPOGRAPHY2.xSmall}
  color: var(--text2);
`;

const CHARACTER_COUNT_LIMIT_STYLE = css`
  label: CHARACTER_COUNT_LIMIT_STYLE;
  color: var(--red);
`;

const BUTTON_ROW_STYLE = css`
  label: BUTTON_ROW_STYLE;
  display: flex;
  gap: ${LAYOUT2.spacing1}px;
  justify-content: flex-end;
`;

const EMPTY_STATE_STYLE = css`
  label: EMPTY_STATE_STYLE;
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1;
  color: var(--text2);
  ${TYPOGRAPHY2.small}
  font-style: italic;
`;

const getNoteStyle = (nodeType: ScanNoteType) => {
  switch (nodeType) {
    case 'approval':
      return NOTE_ITEM_APPROVAL_STYLE;
    case 'editRequest':
      return NOTE_ITEM_EDIT_REQUEST_STYLE;
    case 'editResolution':
      return NOTE_ITEM_EDIT_RESOLUTION_STYLE;
    default:
      return NOTE_ITEM_STYLE;
  }
};

const getNoteTypeBadge = (nodeType: ScanNoteType) => {
  switch (nodeType) {
    case 'approval':
      return <div css={APPROVAL_BADGE_STYLE}>✓ Approved</div>;
    case 'editRequest':
      return <div css={EDIT_REQUEST_BADGE_STYLE}>⚠ Edits Requested</div>;
    case 'editResolution':
      return <div css={EDIT_RESOLUTION_BADGE_STYLE}>↻ Edits Resolved</div>;
    default:
      return null;
  }
};

const getScanPartNoteLabelLookup = (scanRoot: ScanRootOptimized) => {
  const labels: Record<string, string> = {
    [scanRoot.id]: 'SCOPE NOTE',
  };

  for (const group of scanRoot.groups || []) {
    labels[group.id] = 'SCOPE GROUP NOTE';
    for (const task of group.tasks || []) {
      labels[task.id] = 'SCOPE TASK NOTE';
      for (const option of task.options || []) {
        labels[option.id] = 'SCOPE OPTION NOTE';
        for (const lineItem of option.lineItems || []) {
          labels[lineItem.id] = 'SCOPE LINE ITEM NOTE';
        }
      }
    }
  }

  return labels;
};

type DisplayNote = {
  note: ApiScanNote;
  isDeleted: boolean;
};

const NoteItem = ({
  note,
  isDeleted,
  canDelete,
  isDeletePending,
  scanPartNoteLabel,
  currentUserId,
  currentUserName,
  onDelete,
}: {
  note: ApiScanNote;
  isDeleted: boolean;
  canDelete: boolean;
  isDeletePending: boolean;
  scanPartNoteLabel: string;
  currentUserId?: string;
  currentUserName?: string;
  onDelete: (note: ApiScanNote) => Promise<void>;
}) => {
  const { user, isLoading: userLoading } = useUser(note.userId);

  const userName = user?.name
    || (note.userId === currentUserId ? currentUserName : undefined)
    || (userLoading ? 'Loading...' : 'Unknown User');

  return (
    <div
      css={[
        NOTE_ITEM_BASE_STYLE,
        isDeleted ? DELETED_NOTE_STYLE : getNoteStyle(note.scanNoteType),
      ]}
    >
      <div css={NOTE_TITLE_ROW_STYLE}>
        <div css={NOTE_META_ROW_STYLE}>
          {isDeleted ? <div css={DELETED_NOTE_BADGE_STYLE}>DELETED NOTE</div> : (
            getNoteTypeBadge(note.scanNoteType) || <div css={SCOPE_NOTE_BADGE_STYLE}>{scanPartNoteLabel}</div>
          )}
          {isDeleted && (
            <div css={DELETED_EXPLAINER_STYLE}>
              (This note will not be visible on documents)
            </div>
          )}
        </div>
        {canDelete && (
          <button
            type='button'
            css={DELETE_NOTE_BUTTON_STYLE}
            onClick={() => onDelete(note)}
            disabled={isDeletePending}
            aria-label='Delete note'
          >
            <TrashIcon size={28} />
          </button>
        )}
      </div>
      <div css={NOTE_BODY_STYLE}>{note.body}</div>
      <div css={NOTE_HEADER_STYLE}>
        {userName} on {formatDateTime(note.createdAt)}
      </div>
    </div>
  );
};

const getPlaceholder = (allowedNoteTypes: ScanNoteType[]) => {
  if (allowedNoteTypes.length === 1 && allowedNoteTypes[0] !== 'info') {
    if (allowedNoteTypes[0] === 'approval') {
      return 'Include note with approval...';
    }
    if (allowedNoteTypes[0] === 'editRequest') {
      return 'Requested changes...';
    }
  }
  return 'Add a note...';
};

export const NotesPanel = ({
  scanMutators,
  open,
  flyOutClosed,
  scanRoot,
  scanPart,
  scan,
  allowedNoteTypes = ['info'],
}: NotesPanelProps) => {
  const [noteText, setNoteText] = useState('');
  const [deletingNoteIds, setDeletingNoteIds] = useState<
    Record<string, boolean>
  >({});
  const { showToast } = useToast();
  const confirm = useConfirmationModal();
  const { userId, role, user } = useAuth();
  const notesHistoryRef = useRef<HTMLDivElement>(null);
  const notesContentRef = useRef<HTMLDivElement>(null);
  const shouldStickToBottomRef = useRef(true);
  const { projectIsEditable, scanNotes: allNotes } = useScanEditingContextSlice([
    'projectIsEditable',
    'scanNotes',
  ]);
  const { scanPartNotes, scanNoteMutators } = useScanPartNotes(scanPart?.id);

  const scanNotes = scanPart?.id ? scanPartNotes : allNotes;

  const sortedNotes = useMemo(
    () =>
      [...scanNotes].sort(
        (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
      ),
    [scanNotes],
  );
  const scanPartNoteLabelLookup = useMemo(
    () => getScanPartNoteLabelLookup(scanRoot),
    [scanRoot],
  );
  const displayNotes = useMemo<DisplayNote[]>(() => {
    const deletedParentIds = getDeletedParentScanNoteIds(sortedNotes);
    return sortedNotes
      .filter(note => note.scanNoteType !== 'delete')
      .map(note => ({
        note,
        isDeleted: deletedParentIds.has(note.id),
      }));
  }, [sortedNotes]);

  // Stick-to-bottom when notes change (user is viewing latest)
  useLayoutEffect(() => {
    if (shouldStickToBottomRef.current) scrollToBottom(notesHistoryRef.current);
  }, [sortedNotes.length]);

  // Reset scroll to bottom when opening or switching scan/scanPart. Defer until drawer has mounted (FlyOut renders one commit late).
  useEffect(() => {
    if (!open) return;
    shouldStickToBottomRef.current = true;
    let cancelled = false;
    runAfterDrawerReady(() => {
      if (!cancelled) scrollToBottom(notesHistoryRef.current);
    });
    return () => {
      cancelled = true;
    };
  }, [open, scan.id, scanPart?.id]);

  // ResizeObserver: set up only when open, after drawer ready (refs are null until FlyOut shows content)
  useEffect(() => {
    if (!open) return;
    let resizeObserver: ResizeObserver | null = null;
    const frameId = requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const scrollContainerElement = notesHistoryRef.current;
        const contentElement = notesContentRef.current;
        if (!scrollContainerElement || !contentElement) return;
        resizeObserver = new ResizeObserver(() => {
          if (shouldStickToBottomRef.current) {
            scrollToBottom(notesHistoryRef.current);
          }
        });
        resizeObserver.observe(scrollContainerElement);
        resizeObserver.observe(contentElement);
      });
    });
    return () => {
      cancelAnimationFrame(frameId);
      resizeObserver?.disconnect();
    };
  }, [open]);

  const onScroll = useCallback(() => {
    shouldStickToBottomRef.current = isScrolledToBottom(notesHistoryRef.current);
  }, []);

  const addNoteFromForm = async (
    noteType:
      | 'info'
      | 'approval'
      | 'editRequest'
      | 'editResolution'
      | 'instructions',
  ) => {
    if (!noteText.trim()) return;
    if (!userId) {
      showToast('User not authenticated', 'error');
      return;
    }
    const noteData = {
      scanId: scan.id,
      scanPartId: scanPart?.id || scanRoot.id,
      body: noteText.trim(),
      userId,
      scanNoteType: noteType,
    };

    // switch to inReview if the scan is pending review
    if (
      (noteType === 'approval' || noteType === 'editRequest')
      && scan.scanLifecycleStatus === 'pendingReview'
    ) {
      await scanMutators.update(scan.id, {
        scanLifecycleStatus: 'inReview',
      });
    }

    await scanNoteMutators.create(noteData).then(
      () => setNoteText(''),
      error => {
        logError('Failed to create note', error, { scanId: scan.id, noteType });
        showToast('Failed to create note. Please try again.', 'error');
      },
    );
    if (noteType !== 'info') {
      flyOutClosed();
    }
  };

  const deleteNote = useCallback(
    async (note: ApiScanNote) => {
      if (!userId) {
        showToast('User not authenticated', 'error');
        return;
      }

      await confirm({
        title: 'Delete Note',
        message: 'Mark this note as deleted? This note will not be visible on documents.',
        confirmLabel: 'Delete Note',
        cancelLabel: 'Cancel',
        onConfirm: async () => {
          setDeletingNoteIds(prev => ({ ...prev, [note.id]: true }));
          try {
            await scanNoteMutators.create({
              scanPartId: note.scanPartId || scanPart?.id || scanRoot.id,
              scanNoteType: 'delete',
              parentScanNoteId: note.id,
              body: '',
            });
          } catch (error) {
            logError('Failed to delete note', error, {
              scanId: scan.id,
              noteId: note.id,
            });
            showToast('Failed to delete note. Please try again.', 'error');
          } finally {
            setDeletingNoteIds(prev => ({ ...prev, [note.id]: false }));
          }
        },
      });
    },
    [
      userId,
      confirm,
      scanNoteMutators,
      scanPart?.id,
      scanRoot.id,
      scan.id,
      showToast,
    ],
  );

  const title = scanPart ? `Notes - ${scanPart.template.name}` : `All Notes`;

  return (
    <FlyOut open={open} flyOutClosed={flyOutClosed} title={title}>
      <div css={NOTES_CONTAINER_STYLE}>
        <div
          css={NOTES_HISTORY_STYLE}
          ref={notesHistoryRef}
          onScroll={onScroll}
        >
          <div ref={notesContentRef}>
            {displayNotes.length === 0 ? <div css={EMPTY_STATE_STYLE}>No notes yet</div> : (
              displayNotes.map(({ note, isDeleted }, index) => (
                <NoteItem
                  key={`${note.id || index}`}
                  note={note}
                  isDeleted={isDeleted}
                  // Only a remark itself can be deleted - not an edit row (deleting an edit changes
                  // nothing, since edits fold into their note) and not a status note.
                  canDelete={projectIsEditable
                    && !isDeleted
                    && isAuthoredRemarkScanNote(note)
                    && canUserDeleteScanNote({ userId, role }, note)}
                  isDeletePending={!!deletingNoteIds[note.id]}
                  scanPartNoteLabel={scanPartNoteLabelLookup[note.scanPartId || scanRoot.id]
                    || 'SCOPE NOTE'}
                  currentUserId={userId}
                  currentUserName={user?.name}
                  onDelete={deleteNote}
                />
              ))
            )}
          </div>
        </div>

        {projectIsEditable && (
          <div css={FIXED_INPUT_AREA_STYLE}>
            <textarea
              css={TEXTAREA_STYLE}
              value={noteText}
              onChange={e => setNoteText(e.target.value)}
              placeholder={getPlaceholder(allowedNoteTypes)}
              rows={3}
              maxLength={NOTES_CHARACTER_LIMIT}
            />
            <div
              css={[
                CHARACTER_COUNT_STYLE,
                noteText.length >= NOTES_CHARACTER_LIMIT
                && CHARACTER_COUNT_LIMIT_STYLE,
              ]}
            >
              {noteText.length} / {NOTES_CHARACTER_LIMIT}
            </div>

            <div css={BUTTON_ROW_STYLE}>
              {allowedNoteTypes.includes('info') && (
                <Button
                  buttonStyle='gray'
                  onClick={() => addNoteFromForm('info')}
                  disabled={!noteText.trim()}
                >
                  Add Note
                </Button>
              )}
              {allowedNoteTypes.includes('approval') && (
                <Button
                  buttonStyle='green'
                  disabled={!noteText.trim()}
                  onClick={() => addNoteFromForm('approval')}
                >
                  Approve
                </Button>
              )}
              {allowedNoteTypes.includes('editRequest') && (
                <Button
                  buttonStyle='yellow'
                  disabled={!noteText.trim()}
                  onClick={() => addNoteFromForm('editRequest')}
                >
                  Request Edits
                </Button>
              )}
              {allowedNoteTypes.includes('editResolution') && (
                <Button
                  buttonStyle='green'
                  disabled={!noteText.trim()}
                  onClick={() => addNoteFromForm('editResolution')}
                >
                  Requested Changes Resolved
                </Button>
              )}
              {allowedNoteTypes.includes('instructions') && (
                <Button
                  buttonStyle='green'
                  disabled={!noteText.trim()}
                  onClick={() => addNoteFromForm('instructions')}
                >
                  Add Instructions
                </Button>
              )}
            </div>
          </div>
        )}
      </div>
    </FlyOut>
  );
};
